import { useEffect, useReducer, useRef } from "react";
import { Icon } from "./Icon";
import { NOTE_CLOSED, noteReducer } from "../lib/setNoteState";

/** W93 (his word, 2026-09-28): the client's note on a set is NOT printed in the table — "need to be in a comment icon button
 *  and show only when we hover over it or when we click it to open a small window of the comment". No reply per set (his
 *  bound: the exercise's one comment and the overall review stay the two places a coach answers).
 *
 *  🔴 W110 (his report, 2026-09-30: "only show when hovering over and hide when not. on the mobile version … it will just be
 *  stuck and not hide again when we tap somewhere else") SUPERSEDES W93's click-pin. MEASURED before: a mouse click left the
 *  note open after the pointer left (the pin AND `:focus-within`), and on a touch screen a tap elsewhere left it open (the
 *  pin), a second tap too (a sticky `:hover` and `:focus-within`). Now ONE rule decides (`noteReducer`) and the stylesheet
 *  shows the note ONLY from its state: a mouse shows it while over the icon or the note and a click pins nothing; a finger's
 *  tap toggles it and a tap anywhere else, Escape or a scroll hides it; keyboard focus shows it and blur hides it. */
export function SetNote({ note, setWords }: { note: string; setWords: string }) {
  const [state, dispatch] = useReducer(noteReducer, NOTE_CLOSED);
  const wrap = useRef<HTMLSpanElement>(null);
  /** The pointer that pressed the button — a click event does not say whether it came from a mouse or a finger. */
  const pressedWith = useRef<string>("mouse");

  /* While it is open, a press ANYWHERE outside it, a scroll anywhere (capture: the review scrolls inside panes too) and
     Escape close it. Listening only while open keeps a page of notes from holding a listener each for nothing. */
  useEffect(() => {
    if (!state.open) return;
    const onDown = (e: PointerEvent) => {
      if (wrap.current !== null && !wrap.current.contains(e.target as Node)) dispatch({ type: "outside" });
    };
    const onScroll = () => { dispatch({ type: "scroll" }); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") dispatch({ type: "escape" }); };
    document.addEventListener("pointerdown", onDown, true);
    window.addEventListener("scroll", onScroll, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("scroll", onScroll, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [state.open]);

  return (
    <span ref={wrap} className={`review-note${state.open ? " is-open" : ""}`}
          onPointerEnter={(e) => { dispatch({ type: "enter", pointer: e.pointerType }); }}
          onPointerLeave={(e) => { dispatch({ type: "leave", pointer: e.pointerType }); }}
          onKeyDown={(e) => { if (e.key === "Escape" && state.open) { e.stopPropagation(); dispatch({ type: "escape" }); } }}>
      <button type="button" className="review-note-button" aria-expanded={state.open}
              aria-label={`Their note on ${setWords}`}
              onPointerDown={(e) => { pressedWith.current = e.pointerType; }}
              onClick={() => { dispatch({ type: "click", pointer: pressedWith.current }); pressedWith.current = "mouse"; }}
              onFocus={(e) => { dispatch({ type: "focus", visible: e.currentTarget.matches(":focus-visible") }); }}
              onBlur={() => { dispatch({ type: "blur" }); }}>
        <Icon name="message" size={14} />
      </button>
      <span className="review-note-pop" role="note">“{note}”</span>
    </span>
  );
}
